import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { FileText } from 'lucide-react';
import { apiClient } from '../../utils/apiHelper';
import Pagination from '@/components/Pagination';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const IncomeReport = () => {
  const [incomes, setIncomes] = useState([]);
  const [summary, setSummary] = useState({ total: 0, byCategory: {} });
  const [loading, setLoading] = useState(false);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  
  // Pagination State
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const [totalItems, setTotalItems] = useState(0);

  useEffect(() => { fetchIncomes(); }, [currentPage, itemsPerPage]);

  const fetchIncomes = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('page', currentPage);
      params.append('limit', itemsPerPage);
      if (fromDate) params.append('from_date', fromDate);
      if (toDate) params.append('to_date', toDate);
      
      const response = await apiClient.get(`/api/accounts/incomes?${params.toString()}`);
      
      setIncomes(Array.isArray(response.data) ? response.data : []);
      const pagination = response.pagination || {};
      setTotalItems(pagination.total || 0);
      const serverStats = pagination.stats || {};
      setSummary({
        total: serverStats.total_amount || 0,
        byCategory: serverStats.by_category || {}
      });
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
  };

  const handleFilterApply = () => {
    setCurrentPage(1);
    fetchIncomes();
  };

  return (
    <div className="p-6 space-y-6">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Income Report</CardTitle>
          <div className="flex gap-4 items-end" data-testid="income-report-filters">
            <div><Label>From Date</Label><Input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} data-testid="income-from-date" /></div>
            <div><Label>To Date</Label><Input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} data-testid="income-to-date" /></div>
            <Button onClick={handleFilterApply} disabled={loading} data-testid="income-filter-btn"><FileText className="w-4 h-4 mr-2" />{loading ? 'Loading...' : 'Generate'}</Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div className="p-4 bg-green-50 rounded-lg">
              <p className="text-sm text-gray-600">Total Income</p>
              <p className="text-2xl font-bold text-green-600">₹{summary.total.toFixed(2)}</p>
            </div>
            <div className="p-4 bg-blue-50 rounded-lg">
              <p className="text-sm text-gray-600">Total Transactions</p>
              <p className="text-2xl font-bold text-blue-600">{totalItems}</p>
            </div>
            <div className="p-4 bg-purple-50 rounded-lg">
              <p className="text-sm text-gray-600">Categories</p>
              <p className="text-2xl font-bold text-purple-600">{Object.keys(summary.byCategory).length}</p>
            </div>
          </div>

          <div className="mb-6">
            <h3 className="text-lg font-semibold mb-3">Income by Category</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {Object.entries(summary.byCategory).map(([category, amount]) => (
                <div key={category} className="p-3 border rounded-lg">
                  <p className="text-sm text-gray-600">{category}</p>
                  <p className="text-lg font-bold">₹{amount.toFixed(2)}</p>
                </div>
              ))}
            </div>
          </div>

          <h3 className="text-lg font-semibold mb-3">Income Transactions</h3>
          <table className="w-full border-collapse">
            <thead>
              <tr className="border-b">
                <th className="text-left p-3">Date</th>
                <th className="text-left p-3">Category</th>
                <th className="text-left p-3">Amount</th>
                <th className="text-left p-3">Payment Mode</th>
                <th className="text-left p-3">Description</th>
              </tr>
            </thead>
            <tbody>
              {incomes.length === 0 ? (
                <tr><td colSpan="5" className="text-center p-4 text-gray-500">No income records found.</td></tr>
              ) : (
                incomes.map((income) => (
                  <tr key={income.id} className="border-b hover:bg-gray-50">
                    <td className="p-3">{income.income_date}</td>
                    <td className="p-3">{income.category}</td>
                    <td className="p-3 font-semibold text-green-600">₹{income.amount}</td>
                    <td className="p-3">{income.payment_mode}</td>
                    <td className="p-3">{income.description}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
          
          {/* Pagination */}
          <Pagination
            currentPage={currentPage}
            totalPages={Math.ceil(totalItems / itemsPerPage)}
            totalItems={totalItems}
            itemsPerPage={itemsPerPage}
            onPageChange={handlePageChange}
            onItemsPerPageChange={handleItemsPerPageChange}
          />
        </CardContent>
      </Card>
    </div>
  );
};

export default IncomeReport;